import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaLink } from '@react-icons/all-files/fa/FaLink';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { DesignButton, EditButton, RenderActions, WorkflowEmpresaButton } from '../../../../commons/DataGridActions';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities } from '../../../../manager/workflow-empresa-actividad.reducer';
import UserInfo from '../../../../commons/UserInfo';
import LinkCell from '../../../../commons/LinkCell';
import ShieldIcon from '../../../../commons/ShieldIcon';

const BASE = '/configuracion/workflow/workflow-empresa-actividad';

/** List rows often omit `empresa.id`; FK is usually on `workflowEmpresa.empresaId`. */
function resolveEmpresaId(row: any): string | number | undefined {
  const emp = row.workflowEmpresa?.empresa;
  const fromEmp = emp?.id;
  const fromWfe = row.workflowEmpresa?.empresaId;
  if (fromEmp != null) return fromEmp;
  if (fromWfe != null) return fromWfe;
  return undefined;
}

export default function WorkflowEmpresaActividadPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.workflowEmpresaActividad.entities);
  const totalItems = useAppSelector(s => s.workflowEmpresaActividad.totalItems);
  const loading = useAppSelector(s => s.workflowEmpresaActividad.loading);

  const [rows, setRows] = useState<any[]>([]);

  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    dispatch(getEntities(params));
  };

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.5 },
    {
      field: 'workflowEmpresa.empresa.grupoEmpresarial.alias',
      headerName: 'Grupo',
      flex: 2,
      renderCell: (params: any) => {
        const emp = params.row.workflowEmpresa?.empresa;
        const alias = emp?.grupoEmpresarial?.alias;
        const grupoId = emp?.grupoEmpresarial?.id;
        return (
          <LinkCell
            label={alias ?? null}
            filterName="grupoId"
            filterValue={grupoId}
          />
        );
      },
    },
    {
      field: 'rfc',
      headerName: 'RFC',
      flex: 2,
      renderCell: (params: any) => {
        const emp = params.row.workflowEmpresa?.empresa;
        const empresaId = resolveEmpresaId(params.row);
        if (!emp) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
        return (
          <LinkCell
            label={emp.rfc ?? '—'}
            filterName="empresaId"
            filterValue={empresaId}
          />
        );
      },
    },
    {
      field: 'workflowEmpresa.empresa.razonSocial',
      headerName: 'Razon Social',
      flex: 2,
      renderCell: (params: any) => {
        const emp = params.row.workflowEmpresa?.empresa;
        const empresaId = resolveEmpresaId(params.row);
        if (!emp) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
        return (
          <LinkCell
            label={emp.razonSocial ?? null}
            filterName="empresaId"
            filterValue={empresaId}
          />
        );
      },
    },
    {
      field: 'workflowEmpresa.workflow.nombre',
      headerName: 'Workflow',
      flex: 2,
      renderCell: (params: any) => (
        <LinkCell
          label={params.row.workflowEmpresa?.workflow?.nombre}
          filterName="workflowEmpresa.workflow.id"
          filterValue={params.row.workflowEmpresa?.workflow?.id}
        />
      ),
    },
    {
      field: 'workflowNodo.profaseActividad.descripcion',
      headerName: 'Actividad',
      flex: 2,
      renderCell: (params: any) =>
        params.row.workflowNodo?.profaseActividad?.descripcion ?? '-'

    },
    {
      field: 'activo',
      headerName: 'Activo',
      flex: 0.6,
      renderCell: (params: any) =>

        (params.row.workflowEmpresa?.disenoPersonalizado === true ? params.row.habilitado : params.row.workflowNodo?.habilitado)
          ? <FaCheck style={{ color: '#22c55e' }} />
          : <FaTimes style={{ color: '#ef4444' }} />,
    },
    {
      field: 'usuario.id',
      headerName: 'Usuario',
      flex: 2,
      renderCell: (params: any) => (
        <UserInfo
          usuario={params.row.usuario}
          onClick={(u) => {
            const p = new URLSearchParams(searchParams);
            p.set('usuario.id', String(u.id));
            p.set('page', '1');
            setSearchParams(p);
          }}
        />
      ),
    },
    {
      field: 'requiereAutorizacion',
      headerName: 'Requiere Autorización',
      flex: 1.2,
      renderCell: (params: any) => { return <ShieldIcon show={true} strokeColor={params.row?.requiereAutorizacion ? 'black' : '#aaaaaa'} color={params.row?.requiereAutorizacion ? '#f59e0b' : '#cccccc'} ></ShieldIcon > },
    },
    {
      field: 'autoriza.id',
      headerName: 'Autoriza',
      flex: 2,
      renderCell: (params: any) => (
        <UserInfo
          usuario={params.row.autoriza}
          onClick={(u) => {
            const p = new URLSearchParams(searchParams);
            p.set('autoriza.id', String(u.id));
            p.set('page', '1');
            setSearchParams(p);
          }}
        />
      ),
    },
    {
      field: '__actions',
      headerName: '',
      flex: 0.6,
      renderCell: (params: any) => (
        <RenderActions>
          <WorkflowEmpresaButton onClick={() => navigate(`/configuracion/workflow/workflow-empresa?id=${params.row.workflowEmpresa.id}`)} title="Workflow Empresa" />
          <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
        </RenderActions>
      ),
    },
  ];

  // Filter names match the API query params: empresa_id and workflow_id
  const filters = [
    { label: 'Empresa ID', name: 'empresa_id' },
    { label: 'Empresa Id', name: 'empresaId' },
    { label: 'Workflow ID', name: 'workflow_id' },
    { label: 'Usuario ID', name: 'usuario.id' },
    { label: 'Autoriza ID', name: 'autoriza.id' },
    { label: 'Workflow Empresa Id', name: 'workflowEmpresa.id' },
    { label: 'Tipo Nodo', name: 'workflowNodo.tipoNodo' },
    { label: 'Grupo', name: 'workflowEmpresa.empresa.grupoEmpresarial.alias' },
    { label: 'Grupo ID', name: 'grupoId' },
    { label: 'RFC', name: 'workflowEmpresa.empresa.rfc' },
    { label: 'Razón Social', name: 'workflowEmpresa.empresa.razonSocial' },
    { label: 'Workflow', name: 'workflowEmpresa.workflow.nombre' },
    { label: 'Workflow ID', name: 'workflowEmpresa.workflow.id' },
    { label: 'Actividad', name: 'workflowNodo.profaseActividad.descripcion' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Workflow Empresa Actividad', icon: <FaLink /> },
      ]} />

      <PageHeader
        icon={<FaLink />}
        iconColor="#84cc16"
        title="Workflow Empresa Actividad"
        subtitle="Actividades asociadas a empresas por workflow"
      />

      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
